import { Injectable } from '@angular/core';
import { AuthenticationService } from '../authentication/authentication.service';
import { EventsService } from '../event/event.service';
import { UserSessionService } from '../user-session/user-session.service';
import { catchError, debounceTime, distinctUntilChanged, filter, pairwise, switchMap, tap } from 'rxjs/operators';
import { CustomerModel } from 'src/app/models/customer-model';
import { isUndefined } from '../codeUtils';
import { EMPTY } from 'rxjs';
import { StoredGeoResponse } from 'src/app/interfaces/geo-interface';
import { ConfigService } from '../config/config.service';
import { GeoLocationStrategyService } from '../geo-location-strategy/geo-location-strategy.service';
import { GeoLocationControllerService } from '../geo-location/geo-location-controller.service';
import { UtilsService } from '../utils/utils.service';
import { JurisdictionService } from '../geo/jurisdiction.service';

@Injectable({
  providedIn: 'root'
})
export class SessionManagerService {

  constructor(
    private authenticationService: AuthenticationService,
    private events: EventsService,
    private configService: ConfigService,
    private utilsService: UtilsService,
    private jurisdiction: JurisdictionService,
    private geoLocationControllerService: GeoLocationControllerService,
    private geoLocationStrategyService: GeoLocationStrategyService,
    private userSession: UserSessionService
  ) {
  }

  public loggedInState$ = this.userSession.sessionState$.pipe(
    filter(state => !!state.sessionToken && !!state.customerId),
    debounceTime(500),
    distinctUntilChanged((a, b) =>
      a.sessionToken === b.sessionToken && a.customerId === b.customerId
    )
  );

  public logout$ = this.userSession.sessionToken$.pipe(
    pairwise(),
    filter(([prev, curr]) => !!prev && !curr),
    debounceTime(50)
  );

  public checkSession(): void {
    const avoidCheckSessionArray = ['#show-page', '#paypal', '#interac-return'];
    if (avoidCheckSessionArray.some(h => document.location.hash.includes(h))) return;

    const userStatus = { sessionToken: "", customerId: null };

    this.authenticationService.checkSession().then(({ subject, sessionToken }) => {
      if (!subject) {
        this.userSession.clear();
        return;
      }

      subject.pipe(
        tap(response => {
          if (!isUndefined(response.result) && !!response.result) {
            this.userSession.setSessionToken(sessionToken);
            userStatus.sessionToken = sessionToken;
            //Handle Web Refresh to fetch cached Geo Response from Server - Only For Web
             if (!this.utilsService.getPlatformInfo().isMobileApp) {
                if (!!response.payload.allowRealMoney && !isUndefined(response.payload.allowRealMoney)) {
                  if (!isUndefined(response.payload.nextPeriodicGeolocationCheck)) {
                    if(this.configService.getConfig().FEATURE_GEOLOCATION_GEOCOMPLY_RELOAD_CACHE_GEO_RESPONSE === '1')this.setCachedGeoResponse(response.payload)
                  }
                }
              }
          }
        }),
        switchMap(() => this.userSession.refreshCustomer()),
        tap((customer: CustomerModel) => {
          userStatus.customerId = customer.customerId;
          this.userSession.setUserStatus(userStatus);

          this.events.publish("event:appInit:init", {
            triggeredBy: "REFRESH",
          });
        }),
        catchError((error) => {
          if (
            error &&
            "error_code" in error &&
            error.error_code === "hasExceededDepositLimit"
          ) {
            this.userSession.clear();
          }
          return EMPTY;
        })
      ).subscribe();
    });
  }

   async setCachedGeoResponse(data){
    const expiryTsString = data.nextPeriodicGeolocationCheck;  
   
    const currentTime = new Date().getTime();
    
    // Parse the formatted date string into a Date object
    const expiryDate = new Date(expiryTsString);

    // Get the timestamp in milliseconds
    const remainingTimeMs = expiryDate.getTime() - currentTime;

    const remainingTimeSec = remainingTimeMs / 1000;
    const cache_valid_until = new Date().getTime() + remainingTimeMs;
    console.log(`Cache Geo Valid until: ${remainingTimeMs} ms`);
    console.log(`Cache Geo Valid until: ${remainingTimeSec} Seconds`);
    console.log(`Cache Geo Valid until: ${remainingTimeSec / 60} Minutes`);
    const geoDataFromServer: StoredGeoResponse = {
      geoResponse: {
        is_allowed: true
      },
      expiryTime: remainingTimeMs,
      expiry_ts: cache_valid_until,
    }

   if (this.geoLocationStrategyService.isGeoOptimizationEnabled()) {
      try {
        await this.geoLocationControllerService.updateGeoLocationCachedResponseFromServer(geoDataFromServer);
      } catch (err) {
        console.log('Error ReInitialize the geoLocationControllerService service', err)
      }
    } else {
      try {
        await this.jurisdiction.initialize();
      } catch (err) {
        console.log('Error ReInitialize the juridsction service', err)
      }
      try {
        await this.jurisdiction.updateGeoLocationCachedResponseFromServer(geoDataFromServer)
      } catch (err) {
        console.log('Error updating GeoLocation cached response from server', err)
      }
    }
  }

}
